/**
 * riddles.js
 * Four planetary riddles — one per locked gear.
 * Mars, Venus, Saturn, Jupiter.
 *
 * checkAnswer(riddleKey, input) — returns { correct: bool, riddle }
 */

export const RIDDLES = {
  mars: {
    planet: 'Mars',
    glyph: '♂',
    lines: [
      'I wear red like a wound that never closes.',
      'I named a month for the soldiers who march in spring.',
      'Every fist clenched in the dark calls my name.',
      'I am the first fire — not warmth, but want.',
      'What am I?',
    ],
    answers: ['mars', 'ares', 'the red planet'],
    hints: [
      'Red as blood. The day between Sun and Mercury bears my name — Tuesday in the old tongue, Tiw\'s day, the war god\'s day.',
      'I am the fourth planet from the sun. The one that glares.',
    ],
  },

  venus: {
    planet: 'Venus',
    glyph: '♀',
    lines: [
      'I rise before the sun and call myself the morning.',
      'I linger after dusk and call myself the evening.',
      'I am the same star both times — they did not know for centuries.',
      'I am what you want when you want it beautifully.',
      'What am I?',
    ],
    answers: ['venus', 'aphrodite', 'morning star', 'evening star'],
    hints: [
      'Love and beauty. The day before Saturn\'s day bears my name — Friday, Frigg\'s day, the goddess who draws all things near.',
      'I am the second planet from the sun. The brightest thing in the sky that is not the sun or moon.',
    ],
  },

  saturn: {
    planet: 'Saturn',
    glyph: '♄',
    lines: [
      'I am the weight you feel when you have run out of excuses.',
      'I am the father of the father, the one who ate his children whole.',
      'My rings are beautiful from a distance.',
      'Up close, they are ice and stone — old debris, circling endlessly.',
      'What am I?',
    ],
    answers: ['saturn', 'kronos', 'cronus', 'father time'],
    hints: [
      'Rings. Limits. Discipline. My day ends the week — Saturday, the sabbath, the day of rest and reckoning.',
      'I am the sixth planet from the sun. The last one the ancients could see without instruments.',
    ],
  },

  jupiter: {
    planet: 'Jupiter',
    glyph: '♃',
    lines: [
      'I am the excess before the lesson.',
      'I am the luck you did not earn but somehow deserved.',
      'The Greeks called me the sky father. The Romans gave me thunder.',
      'I am so large that all the other planets could fit inside me twice.',
      'What am I?',
    ],
    answers: ['jupiter', 'zeus', 'jove'],
    hints: [
      'The biggest. The luckiest. Thursday — Thor\'s day, the thunder god\'s day — is mine.',
      'I am the fifth planet from the sun. The largest in this system. The one that guards the inner worlds from passing rocks.',
    ],
  },
};

/**
 * Check a user's answer against a riddle's accepted answers.
 * Case-insensitive. Uses includes() so partial matches work for multi-word answers.
 *
 * @param {string} riddleKey - One of: 'mars', 'venus', 'saturn', 'jupiter'
 * @param {string} input - The user's raw answer string
 * @returns {{ correct: boolean, riddle: object }}
 */
export function checkAnswer(riddleKey, input) {
  const riddle = RIDDLES[riddleKey];
  if (!riddle) return { correct: false, riddle: null };

  const normalized = input.trim().toLowerCase();
  const correct = riddle.answers.some(answer => normalized.includes(answer));

  return { correct, riddle };
}
